import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BadgeCheck, Star, ShieldCheck, ArrowDownRight } from "lucide-react";
import { LeadForm } from "./LeadForm";
import { scrollToId } from "@/App";

const HERO_BG =
  "https://images.unsplash.com/photo-1525367922492-f15fe7b709cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGZpbmFuY2lhbCUyMGdyb3d0aCUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODYyMDYwMDR8MA&ixlib=rb-4.1.0&q=85";

const lines = ["Investment guidance", "built around", "your goals."];

const badges = [
  { icon: BadgeCheck, label: "AMFI Registered", testid: "hero-badge-amfi" },
  { icon: Star, label: "NISM Certified", testid: "hero-badge-nism" },
  { icon: ShieldCheck, label: "ARN-184789", testid: "hero-badge-arn" },
];

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="grain relative min-h-screen bg-wealth-dark overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[15%] h-[130%]">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
      </motion.div>
      <div className="absolute inset-0 bg-wealth-dark/70" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(103,37,244,0.14),transparent_55%)]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-40 pb-16 lg:pt-48 lg:pb-24 grid lg:grid-cols-12 gap-16 items-center"
      >
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease }}
            className="text-xs font-semibold uppercase tracking-[0.32em] text-brand-light mb-8"
          >
            Madhura Gokhale — Wealth Partner, Pune
          </motion.p>

          <h1
            data-testid="hero-heading"
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem] font-light leading-[1.02] tracking-tight text-bone"
          >
            {lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-brand-light" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.15, delay: 0.4 + i * 0.16, ease }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-bone/70"
          >
            Personalised investment planning, goal-based wealth creation and smart
            tax-saving strategies — for families and individuals who want their money to
            work as hard as they do.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease }}
            className="mt-9 flex flex-wrap gap-3"
          >
            {badges.map(({ icon: Icon, label, testid }) => (
              <span
                key={label}
                data-testid={testid}
                className="inline-flex items-center gap-2 border border-bone/20 text-bone/80 text-[11px] font-semibold uppercase tracking-[0.18em] px-4 py-2.5 rounded-sm backdrop-blur-sm"
              >
                <Icon size={14} className="text-brand-light" />
                {label}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35, ease }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <motion.button
              data-testid="hero-cta-consultation"
              whileTap={{ scale: 0.96 }}
              onClick={() => scrollToId("#consultation")}
              className="bg-brand text-white text-xs font-bold uppercase tracking-[0.2em] px-8 py-4 rounded-sm hover:bg-brand-light transition-colors duration-300"
            >
              Book Free Consultation
            </motion.button>
            <a
              data-testid="hero-cta-invest"
              href="https://wa.me/919730527427?text=Hi%20Madhura%2C%20I%20want%20to%20start%20investing."
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 text-bone text-xs font-bold uppercase tracking-[0.2em] border-b border-brand/60 pb-1 hover:text-brand-light transition-colors duration-300"
            >
              Start Investing
              <ArrowDownRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 48 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.9, ease }}
          className="lg:col-span-5"
        >
          <div className="bg-white/[0.06] backdrop-blur-2xl border border-white/15 rounded-sm p-8 md:p-10 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.6)]">
            <h2 className="font-display text-3xl font-medium text-bone tracking-tight">
              Get a free consultation
            </h2>
            <p className="text-sm text-bone/55 mt-2 mb-8">
              Tell us where to reach you — Madhura calls back personally.
            </p>
            <LeadForm source="hero" dark />
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.7 }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-12"
      >
        <div className="border-t border-bone/10 pt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            ["₹6,000 Cr+", "investments executed"],
            ["1,00,000+", "investors on the platform"],
            ["SEBI", "registered investment platform"],
          ].map(([num, label]) => (
            <div key={label} className="flex items-baseline gap-3">
              <span className="font-display text-3xl md:text-4xl font-medium text-brand-light">{num}</span>
              <span className="text-xs uppercase tracking-[0.18em] text-bone/50">{label}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
